<!-- eslint-disable vue/no-parsing-error -->
<template>
  <div class="excel-import-container">
    <el-page-header content="店面业务数据导入" />
    <el-card class="import-card" shadow="hover">
      <!-- 上传区域：复用基础表导入样式，新增导入人信息 -->
      <div class="upload-section">
        <!-- 导入人信息：取当前登录人，不可编辑 -->
        <div class="importer-info" v-if="importerName">
          <i class="el-icon-user"></i>
          <span class="label">当前导入人：</span>
          <span class="name">{{ importerName }}</span>
        </div>

        <el-upload
          class="excel-uploader"
          drag
          action="#"
          :auto-upload="false"
          :file-list="[]"
          :show-file-list="false"
          accept=".xlsx,.xls"
          :before-upload="beforeUpload"
          :on-change="handleFileChange"
          :on-remove="handleFileRemove"
          :limit="1"
          :on-exceed="handleExceed"
        >
          <i class="el-icon-upload2 upload-icon"></i>
          <div class="el-upload__text">
            将Excel文件拖到此处，或<em>点击上传</em>
          </div>
          <div class="el-upload__tip" slot="tip">
            仅支持.xlsx/.xls格式，文件大小不超过1000MB，当前支持6个Sheet工作表（店面业务专用）
          </div>
        </el-upload>

        <div class="custom-file-list" v-if="fileList.length > 0">
          <div class="file-item">
            <i class="el-icon-file-excel file-icon"></i>
            <span class="file-name" :title="fileList[0].name">{{ fileList[0].name }}</span>
            <el-button
              type="text"
              icon="el-icon-delete"
              class="delete-btn"
              @click="handleFileRemove"
            ></el-button>
          </div>
        </div>

        <div class="btn-group" v-if="fileList.length > 0">
          <el-button
            type="primary"
            icon="el-icon-read"
            @click="parseExcel"
            :loading="isParsing"
            :disabled="!hasToken || isParsing || isImporting"
          >
            解析Excel数据
          </el-button>
          <el-button
            type="success"
            icon="el-icon-upload"
            @click="submitImport"
            :loading="isImporting"
            :disabled="!hasToken || !parsedData.length || isParsing || isImporting"
          >
            提交导入
          </el-button>
          <el-progress 
            v-if="isParsing && parseProgress > 0" 
            :percentage="parseProgress" 
            size="small" 
            style="width: 200px; margin-left: 20px;"
          />
        </div>
      </div>

      <!-- 预览区域：修复Sheet切换+分页逻辑，确保数据正常显示 -->
      <div class="preview-section" v-if="parsedData.length > 0">
        <el-divider content-position="left">
          <i class="el-icon-data-analysis"></i> 数据预览（共{{ parsedData.length }}个Sheet，点击Sheet名称切换）
        </el-divider>
        <el-tabs v-model="activeSheetStr" type="card" class="sheet-tabs" @tab-click="handleTabChange">
          <el-tab-pane
            v-for="(item, index) in parsedData"
            :key="index"
            :label="`${item.originalSheetName}（${item.data.length}条）`"
            :name="index.toString()"
          >
            <el-table
              :data="currentPageData"
              border
              stripe
              size="small"
              max-height="400"
              v-loading="isTableLoading"
              :row-key="(row, idx) => idx"
              :highlight-current-row="false"
            >
              <el-table-column
                v-for="(prop, propIndex) in Object.keys(parsedData[activeSheet].data[0] || {})"
                :key="propIndex"
                :prop="prop"
                :label="prop"
                align="center"
                :width="Math.min(Math.max(prop.length * 8 + 40, 80), 250)"
                show-overflow-tooltip
              />
            </el-table>

            <div class="pagination-container" v-if="parsedData[activeSheet].data.length > 0">
              <el-pagination
                @size-change="handleSizeChange"
                @current-change="handleCurrentChange"
                :current-page="pagination.currentPage"
                :page-sizes="[50, 100, 200, 500]"
                :page-size="pagination.pageSize"
                layout="total, sizes, prev, pager, next, jumper"
                :total="parsedData[activeSheet].data.length"
                background
                small
              />
            </div>

            <div class="data-count-tip" v-if="parsedData[activeSheet].data.length > 1000">
              <i class="el-icon-warning"></i> 当前Sheet数据量较大（{{ parsedData[activeSheet].data.length }}条），已分页展示，可直接提交导入
            </div>
            <div class="data-count-tip info" v-else-if="parsedData[activeSheet].data.length > 500">
              <i class="el-icon-info"></i> 当前Sheet数据量较多（{{ parsedData[activeSheet].data.length }}条），分页展示更流畅，建议直接提交导入
            </div>
          </el-tab-pane>
        </el-tabs>
      </div>
    </el-card>
  </div>
</template>

<script>
import service from '@/utils/request'
import * as XLSX from 'xlsx'
import router from '@/router' // 引入路由，用于登录跳转

/**
 * Excel日期值校正（精准版：区分Excel序列号Date和普通Date）
 * 解决"日期加1过度"问题，与后端逻辑完全对齐
 */
function fixExcelDate(value, isExcelSerialDate = false) {
  // 步骤1：统一转换为有效Date对象
  let dateObj;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) {
      console.warn('fixExcelDate：入参是无效Date对象，返回空字符串', value);
      return '';
    }
    dateObj = value;
  } else if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return '';
    }
    dateObj = parseDateString(trimmedValue);
    if (!dateObj) {
      console.warn('fixExcelDate：字符串无法解析为有效日期', value);
      return '';
    }
  } else {
    console.warn('fixExcelDate：入参类型不支持', typeof value, value);
    return '';
  }

  try {
    let finalTime = dateObj.getTime();

    // 仅对Excel序列号解析的Date补偿1900bug
    if (isExcelSerialDate) {
      finalTime += 86400000; // 1天=24*60*60*1000毫秒
      console.debug('fixExcelDate：执行Excel 1900bug补偿，加1天');
    }

    // 东八区时区校正
    const gmt8Time = new Date(finalTime);
    const year = gmt8Time.getUTCFullYear();
    const month = String(gmt8Time.getUTCMonth() + 1).padStart(2, '0');
    const day = String(gmt8Time.getUTCDate()).padStart(2, '0');

    const fixedDate = `${year}-${month}-${day}`;
    console.debug('Excel日期校正完成：', {
      originalValue: value instanceof Date ? value.toISOString() : value,
      isExcelSerialDate,
      fixedDate
    });
    return fixedDate;

  } catch (error) {
    console.error('Excel日期校正失败：', error, '原始值：', value);
    return dateObj.toISOString().split('T')[0];
  }
}

/**
 * 辅助方法：解析多种格式的日期字符串为Date对象
 * 修复ESLint no-useless-escape警告
 */
function parseDateString(str) {
  const reg = /^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/;
  const match = str.match(reg);
  if (!match) {
    return null;
  }
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1;
  const day = parseInt(match[3], 10);
  const date = new Date(Date.UTC(year, month, day));
  if (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month &&
    date.getUTCDate() === day
  ) {
    return date;
  }
  return null;
}

export default {
  name: 'StoreBusinessImport',
  data() {
    return {
      fileList: [],
      rawFile: null,
      isParsing: false,
      isImporting: false,
      isTableLoading: false,
      parseProgress: 0,
      parsedData: [], // 与基础表一致：存储Sheet数据，格式[{originalSheetName, sheetName, data}]
      activeSheet: 0, // 数字类型：当前激活的Sheet索引
      activeSheetStr: '0', // 字符串类型：适配el-tabs的v-model
      importerName: '', // 新增：导入人（当前登录人）
      pagination: { // 与基础表完全一致的分页参数
        currentPage: 1,
        pageSize: 100
      },
      currentPageData: []
    }
  },
  // 计算属性：与基础表一致，新增导入人校验
  computed: {
    hasToken() {
      const token = localStorage.getItem('accessToken')
      // 校验token存在且非空字符串（与基础表逻辑一致）
      return !!token && token.trim() !== ''
    },
    // 新增：校验导入人是否有效
    hasValidImporter() {
      return !!this.importerName && this.importerName.trim() && this.importerName !== 'undefined'
    }
  },
  watch: {
    // 修复：同时监听数字/字符串类型的Sheet索引，确保同步
    activeSheet: {
      handler(newVal) {
        this.activeSheetStr = newVal.toString()
        this.resetPagination()
        this.setCurrentPageData()
      },
      immediate: true
    },
    activeSheetStr(newVal) {
      this.activeSheet = Number(newVal)
    },
    // 与基础表一致：监听分页参数变化
    pagination: {
      handler() {
        this.setCurrentPageData()
      },
      deep: true
    },
    // 与基础表一致：监听token变化，失效则清空数据
    hasToken(newVal) {
      if (!newVal) {
        this.parsedData = []
        this.fileList = []
        this.rawFile = null
        this.importerName = ''
      }
    },
    // 新增：监听导入人有效性，无效则跳转登录
    hasValidImporter(newVal) {
      if (!newVal && this.hasToken) {
        this.$message.warning('未获取到当前登录人信息，请重新登录！')
        router.push({ path: '/login' })
      }
    }
  },
  created() {
    // 初始化：获取当前登录人（导入人），复用localStorage中的adminName
    this.getImporterName()
  },
  methods: {
    // 新增：获取当前登录人（导入人），与基础表登录逻辑兼容
    getImporterName() {
      const userName = localStorage.getItem('adminName')
      if (userName && userName.trim() && userName !== 'undefined') {
        this.importerName = userName.trim()
      } else {
        this.$message.warning('未获取到当前登录人信息，请重新登录！')
        router.push({ path: '/login' })
      }
    },

    // 与基础表完全一致：通用登录状态校验方法
    checkLoginStatus() {
      if (!this.hasToken) {
        this.$message.error('登录状态已过期，请重新登录！')
        // 跳转到登录页，携带当前页面地址（与基础表逻辑一致）
        router.push({
          path: '/login',
          query: { redirect: this.$route.fullPath }
        })
        return false
      }
      // 新增：校验导入人信息
      if (!this.hasValidImporter) {
        this.$message.error('未获取到导入人信息，请重新登录！')
        router.push({ path: '/login' })
        return false
      }
      return true
    },

    // 与基础表一致：上传前校验（仅调整Sheet数量提示）
    beforeUpload(file) {
      const fileType = file.name.split('.').pop().toLowerCase()
      const isExcel = fileType === 'xlsx' || fileType === 'xls'
      const isLt1000M = file.size / 1024 / 1024 < 1000

      if (!isExcel) {
        this.$message.error('仅支持上传 .xlsx / .xls 格式的Excel文件！')
        return false
      }
      if (!isLt1000M) {
        this.$message.error('Excel文件大小不能超过1000MB！')
        return false
      }
      return true
    },

    // 与基础表完全一致：文件变更处理
    handleFileChange(file, fileList) {
      this.fileList = fileList.slice(-1)
      this.rawFile = this.fileList[0]?.raw || null
      this.parsedData = []
      this.activeSheet = 0
      this.activeSheetStr = '0'
      this.parseProgress = 0
      this.resetPagination()
    },

    // 与基础表完全一致：移除文件处理
    handleFileRemove() {
      this.fileList = []
      this.rawFile = null
      this.parsedData = []
      this.activeSheet = 0
      this.activeSheetStr = '0'
      this.parseProgress = 0
      this.resetPagination()
      this.$message.info('已移除选择的Excel文件')
    },

    // 与基础表完全一致：超出文件数量限制提示
    handleExceed() {
      this.$message.warning('仅支持上传1个Excel文件，请先移除已选择的文件！')
    },

    // 解析Excel：核心修复Sheet数据为空问题，适配6个Sheet场景
    async parseExcel() {
      // 第一步：校验登录状态和导入人信息（与基础表逻辑一致）
      if (!this.checkLoginStatus()) return
      if (!this.rawFile) {
        this.$message.warning('请先选择要解析的Excel文件！')
        return
      }

      this.isParsing = true
      this.parseProgress = 0
      try {
        const arrayBuffer = await this.readFileAsArrayBuffer(this.rawFile)
        const workbook = XLSX.read(arrayBuffer, {
          type: 'array',
          cellDates: true, // 必须开启，转Date对象（与基础表一致）
          cellText: false,
          dateNF: 'yyyy-mm-dd', // 小写mm，避免XLSX.js自动格式化（与基础表一致）
          cellStyles: false,
          sheetStubs: false,
          raw: false // 必须false，否则cellDates失效（与基础表一致）
        })

        const sheetNames = workbook.SheetNames
        const requiredSheetCount = 6 // 调整为6个Sheet（店面业务专用）
        if (sheetNames.length !== requiredSheetCount) {
          this.$message.error(`解析失败！当前Excel包含${sheetNames.length}个Sheet，要求必须为${requiredSheetCount}个（店面业务专用）！`)
          this.isParsing = false
          return
        }

        // 核心修复：移除header:1，改用默认配置解析Sheet数据
        const parsedList = []
        const totalSheets = sheetNames.length
        for (let index = 0; index < totalSheets; index++) {
          const sheetName = sheetNames[index]
          const worksheet = workbook.Sheets[sheetName]
          // 修复：移除header:1，避免表头处理异常导致数据为空
          const sheetData = XLSX.utils.sheet_to_json(worksheet, {
            defval: '',
            blankrows: false // 过滤空行，保留有效数据
          })

          // 处理日期字段：使用精准校正方法，避免加1过度
          const processedData = sheetData.map(row => {
            const rowObj = {}
            Object.keys(row).forEach(key => {
              let value = row[key]
              // 仅对Date类型值做精准校正
              if (value instanceof Date) {
                value = fixExcelDate(value, true)
              }
              // 修复空值处理：保留0/false等有效值
              rowObj[key] = value === undefined || value === null ? '' : value
            })
            return rowObj
          })

          // 与基础表完全一致的parsedData结构
          parsedList.push({
            originalSheetName: sheetName.trim(), // 去除Sheet名前后空格
            sheetName: `Sheet${index + 1}`, // 标准化Sheet名（与基础表一致）
            data: processedData // 解析后的有效数据
          })

          // 确保进度条响应式更新
          this.$nextTick(() => {
            this.parseProgress = Math.round(((index + 1) / totalSheets) * 100)
          })
          await new Promise(resolve => setTimeout(resolve, 50))
        }

        this.parsedData = parsedList
        this.resetPagination()
        this.$message.success(`解析成功！共解析${parsedList.length}个Sheet（店面业务专用），可预览数据后提交导入！`)
      } catch (error) {
        this.$message.error(`Excel解析失败：${error.message || '文件损坏、格式异常或正在被占用'}`)
        console.error('Excel解析异常：', error)
      } finally {
        this.isParsing = false
        this.parseProgress = 0
      }
    },

    // 与基础表完全一致：File转ArrayBuffer方法
    readFileAsArrayBuffer(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = (e) => resolve(e.target.result)
        reader.onerror = () => reject(new Error('Excel文件读取失败，请检查文件是否损坏、未关闭或正在被占用'))
        reader.onabort = () => reject(new Error('文件读取被中断'))
        reader.readAsArrayBuffer(file)
      })
    },

    // 提交导入：核心调整！与基础表统一数据结构，新增导入人参数
    async submitImport() {
      // 第一步：校验登录状态（与基础表逻辑一致）
      if (!this.checkLoginStatus()) return
      if (this.parsedData.length === 0) {
        this.$message.warning('请先解析Excel数据再提交！')
        return
      }

      try {
        // 与基础表一致：计算总数据量
        const totalDataCount = this.parsedData.reduce((total, item) => total + item.data.length, 0)
        const confirmRes = await this.$confirm(
          `确定要提交这6个Sheet的店面业务数据导入吗？共${totalDataCount}条数据，导入人：${this.importerName}，导入后将不可撤回！`,
          '导入确认',
          {
            confirmButtonText: '确定导入',
            cancelButtonText: '取消',
            type: 'warning',
            confirmButtonClass: 'el-button--danger'
          }
        )
        if (confirmRes !== 'confirm') return

        this.isImporting = true
        this.isTableLoading = true
        // 与基础表完全一致的提交数据结构！仅新增importerName字段
        const submitData = {
          excelName: this.fileList[0].name, // 与基础表一致：Excel文件名
          // 与基础表完全一致：sheetDatas结构（字段名、格式完全对齐）
          sheetDataList: this.parsedData.map((item, idx) => ({
            sheetIndex: idx + 1, // 修复：简化Sheet索引计算，避免findIndex性能问题
            originalSheetName: item.originalSheetName, // 与基础表一致：原始Sheet名
            sheetName: item.sheetName, // 与基础表一致：标准化Sheet名
            data: item.data // 与基础表一致：解析后的行数据
          })),
          // 新增：导入人字段（后端需兼容接收）
          importerName: this.importerName
        }
        console.log('准备提交店面业务导入数据（与基础表格式一致）：', submitData)
        
        // 调用店面业务导入接口（路径不同，但数据结构与基础表一致）
        const res = await service.post('excel/import/store-auxiliary', submitData, {
          timeout: 3000000, // 与基础表一致：300秒超时
          onUploadProgress: (progressEvent) => { // 与基础表一致：上传进度监听
            const percent = Math.round((progressEvent.loaded / (progressEvent.total || 1)) * 100)
            console.log('上传进度：', percent, '%')
          }
        })

        if (res && res.code === 200) {
          this.$message.success('店面业务数据导入成功！')
          this.handleFileRemove() // 与基础表一致：导入成功后清空数据
        } else {
          this.$message.error(`导入失败：${res?.msg || '后端处理失败，未返回有效结果'}`)
        }
      } catch (error) {
        // 与基础表完全一致的异常处理逻辑
        if (error === 'cancel' || (error.name === 'Error' && error.message === 'cancel')) {
          this.$message.info('已取消数据导入')
        } else {
          if (error.message !== '登录状态失效，请重新登录') {
            this.$message.error(`导入请求失败：${error.message || '网络异常、请求超时或后端接口未响应，请检查后端服务'}`)
            console.error('店面业务导入请求异常详情：', error)
          }
        }
      } finally {
        this.isImporting = false
        this.isTableLoading = false
      }
    },

    // 与基础表完全一致：重置分页参数
    resetPagination() {
      this.pagination = {
        currentPage: 1,
        pageSize: 100
      }
    },

    // 修复：确保当前页数据正确计算
    setCurrentPageData() {
      if (!this.parsedData.length || !this.parsedData[this.activeSheet]?.data.length) {
        this.currentPageData = []
        return
      }
      const { currentPage, pageSize } = this.pagination
      const startIndex = (currentPage - 1) * pageSize
      const endIndex = currentPage * pageSize
      this.currentPageData = this.parsedData[this.activeSheet].data.slice(startIndex, endIndex)
    },

    // 与基础表完全一致：切换单页展示条数
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.currentPage = 1
    },

    // 与基础表完全一致：切换当前页码
    handleCurrentChange(val) {
      this.pagination.currentPage = val
    },

    // 修复：Sheet标签点击事件，适配字符串类型的tab.index
    handleTabChange(tab) {
      this.activeSheet = Number(tab.index)
      this.activeSheetStr = tab.index
    }
  }
}
</script>

<style scoped>
/* 完全复用基础表导入样式，仅新增导入人信息样式 */
.excel-import-container {
  padding: 20px;
  background-color: #f5f5f5;
  min-height: calc(100vh - 120px);
}

.import-card {
  padding: 30px;
}

/* 新增：导入人信息样式（不影响基础表样式） */
.importer-info {
  margin-bottom: 20px;
  padding: 10px 15px;
  background-color: #f0f9ff;
  border-radius: 6px;
  display: flex;
  align-items: center;
  font-size: 14px;
  color: #333;
}
.importer-info .el-icon-user {
  color: #409eff;
  margin-right: 8px;
  font-size: 16px;
}
.importer-info .label {
  color: #666;
  margin-right: 8px;
}
.importer-info .name {
  font-weight: 500;
  color: #409eff;
}

/* 以下样式与基础表完全一致 */
.upload-section {
  margin-bottom: 30px;
}

.excel-uploader {
  width: 100%;
  height: 200px;
  line-height: 200px;
  text-align: center;
  border: 2px dashed #dcdcdc;
  border-radius: 6px;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}
.excel-uploader:hover {
  border-color: #409eff;
  background-color: #f8f9fa;
}
.upload-icon {
  font-size: 48px;
  color: #409eff;
  margin-bottom: 10px;
  display: block;
  line-height: 1;
}

.custom-file-list {
  width: 100%;
  margin-bottom: 20px;
}
.file-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 15px;
  border: 1px solid #e6e6e6;
  border-radius: 6px;
  background-color: #fff;
}
.file-icon {
  font-size: 20px;
  color: #67c23a;
  margin-right: 12px;
}
.file-name {
  flex: 1;
  font-size: 14px;
  color: #333;
  white-space: nowrap;
  overflow-x: auto;
  text-overflow: ellipsis;
  max-height: 20px;
  padding: 0 2px;
}
.file-name::-webkit-scrollbar {
  height: 4px;
}
.file-name::-webkit-scrollbar-thumb {
  background-color: #dcdcdc;
  border-radius: 2px;
}
.file-name::-webkit-scrollbar-track {
  background-color: #f5f5f5;
}
.delete-btn {
  color: #f56c6c;
  font-size: 16px;
}
.delete-btn:hover {
  color: #f04848;
}

.btn-group {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
}
.btn-group .el-button {
  margin: 0 10px 10px;
}

.preview-section {
  margin-top: 20px;
}
.sheet-tabs {
  margin-bottom: 15px;
}

.pagination-container {
  margin-top: 15px;
  text-align: right;
}

.data-count-tip {
  margin-top: 10px;
  padding: 8px 12px;
  background-color: #fff2f2;
  border: 1px solid #ffccc7;
  border-radius: 4px;
  color: #f56c6c;
  font-size: 12px;
  display: flex;
  align-items: center;
}
.data-count-tip.info {
  background-color: #fffbee;
  border-color: #ffe58f;
  color: #fa8c16;
}
.data-count-tip .el-icon-info,
.data-count-tip .el-icon-warning {
  margin-right: 8px;
}
</style>